import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import type { RecordSourceSelectorProxy } from 'relay-runtime';
import { JiraProjectAri } from '@atlassian/ari/jira/project';
import { get as validateProjectName } from '@atlassian/jira-common-rest/src/simplified/latest/project/validate/project-name/rest.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { renameProjectMutation } from '@atlassian/jira-relay/src/__generated__/renameProjectMutation.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import RelayDataID from '@atlassian/relay-data-id';
import messages from './messages.tsx';

export const useRenameProject = () => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [commitMutation] = useMutation<renameProjectMutation>(graphql`
		mutation renameProjectMutation($cloudId: ID!, $projectIdOrKey: String!, $name: String!) {
			jira {
				updateProjectName(
					input: { cloudId: $cloudId, projectIdOrKey: $projectIdOrKey, name: $name }
				) {
					success
					errors {
						message
						extensions {
							statusCode
							errorType
						}
					}
					project {
						id
						name
					}
				}
			}
		}
	`);

	const updateProjectName = useCallback(
		(projectId: number | undefined, name: string) => {
			if (!projectId) return;
			const newName = name.trim();
			const nameUpdater = (store: RecordSourceSelectorProxy) => {
				const ari = JiraProjectAri.create({
					siteId: cloudId,
					projectId: String(projectId),
				});
				const relayDataId = RelayDataID({ id: ari }, 'JiraProject');
				if (!relayDataId) return;

				const project = store.get(relayDataId);

				if (project) {
					project.setValue(newName, 'name');
				}
			};
			commitMutation({
				variables: {
					cloudId,
					projectIdOrKey: String(projectId),
					name: newName,
				},
				optimisticUpdater: nameUpdater,
				updater: (store, response) => {
					if (response?.jira?.updateProjectName?.success) {
						nameUpdater(store);
					}
				},
				onCompleted: (response) => {
					const { success, errors } = response?.jira?.updateProjectName ?? {};
					const error = errors?.find((e) => e.message);

					if (success) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'projectName updated');
					} else if (error) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'projectName update failed', {
							error,
						});
					}
				},
			});
		},
		[commitMutation, cloudId, createAnalyticsEvent],
	);

	const validate = async (title: string, newValue: string) => {
		try {
			const { errors } = await validateProjectName('', newValue);
			if (errors.projectName) {
				// There's an error if the value is being updated to an existing project
				// name, but we can skip this if the value is the current project name
				if (newValue === title) {
					return undefined;
				}
				return errors.projectName;
			}
			return undefined;
		} catch {
			return formatMessage(messages.updateProjectNameError);
		}
	};

	return {
		updateProjectName,
		validateProjectName: validate,
	};
};
